'use client';

import { useEffect, useRef, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { cx } from '@optego/ui';
import { usePathname, useRouter } from '@/i18n/navigation';
import { localeNames, locales, type Locale } from '@/i18n/config';

/** Language switcher: `compact` dropdown for the header, `list` of chips for the mobile drawer. Keeps page and filters. */
export default function LocaleSwitcher({ variant = 'compact', onChange }: { variant?: 'compact' | 'list'; onChange?: () => void }) {
  const t = useTranslations('common');
  const locale = useLocale() as Locale;
  const pathname = usePathname();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  function select(next: Locale) {
    setOpen(false);
    onChange?.();
    if (next === locale) return;
    // The query string (e.g. catalog filters) is kept; next-intl stores the choice in the NEXT_LOCALE cookie
    router.replace(`${pathname}${window.location.search}`, { locale: next });
  }

  if (variant === 'list') {
    return (
      <div className="locale-list" role="group" aria-label={t('language')}>
        {locales.map((code) => (
          <button
            key={code}
            type="button"
            lang={code}
            className={cx('chip', code === locale && 'chip--active')}
            aria-current={code === locale ? 'true' : undefined}
            onClick={() => select(code)}
          >
            {localeNames[code]}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="locale-menu" ref={menuRef}>
      <button
        type="button"
        className="lang-toggle"
        aria-expanded={open}
        aria-controls="locale-menu-panel"
        aria-label={`${t('language')}: ${localeNames[locale]}`}
        onClick={() => setOpen((value) => !value)}
      >
        {locale.toUpperCase()}
      </button>
      {open && (
        <ul id="locale-menu-panel" className="user-menu__panel locale-menu__panel">
          {locales.map((code) => (
            <li key={code}>
              <button
                type="button"
                lang={code}
                className={cx('user-menu__item', code === locale && 'user-menu__item--active')}
                aria-current={code === locale ? 'true' : undefined}
                onClick={() => select(code)}
              >
                {localeNames[code]}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
